import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useBrand } from '../hooks/useBrand'
import { useToast } from '../context/ToastContext'
import { useTranslation } from '../i18n/LanguageContext'
import ReturnReasonModal from '../components/ReturnReasonModal'

// Legacy const names kept for minimal diff — now mapped to shared CSS design tokens
const BLUE   = 'var(--primary)'
const TEXT   = 'var(--text-primary)'
const MUTED  = 'var(--text-muted)'
const BORDER = 'var(--border)'
const BG     = 'var(--bg-muted)'
const WHITE  = '#FFFFFF'

const STATUSES = [
  { key: 'pre_order',    label: 'Pre-order',    bg: '#F0FDFA', text: '#0D9488' },
  { key: 'received',     label: 'Received',     bg: 'var(--primary-light)', text: BLUE },
  { key: 'pending',      label: 'Pending',      bg: '#FFF8E6', text: '#D97706' },
  { key: 'confirmed',    label: 'Confirmed',    bg: '#FEF3C7', text: '#92400E' },
  { key: 'processing',   label: 'Processing',   bg: '#F0FDFA', text: '#0F766E' },
  { key: 'with_courier', label: 'With Courier', bg: '#F5F3FF', text: '#6D28D9' },
  { key: 'shipped',      label: 'Shipped',      bg: '#ECFDF5', text: '#065F46' },
  { key: 'closed',       label: 'Closed',       bg: '#F5F5F5', text: MUTED },
  { key: 'return',       label: 'Return',       bg: '#FEF2F2', text: '#DC2626' },
]

const STATUS_ICON_KEYS = {
  received: 'inbox', pending: 'clock', confirmed: 'checkCircle',
  processing: 'gear', with_courier: 'truck', shipped: 'mailOpen',
  closed: 'lock', return: 'undo',
}

const STOCK_DEDUCTED_STATUSES = ['confirmed','processing','with_courier','shipped','closed']

const DIVISIONS       = ['Dhaka','Chattogram','Rajshahi','Khulna','Barishal','Sylhet','Rangpur','Mymensingh']
const COURIERS        = ['Pathao','Steadfast','RedX','Sundarban','eCourier','Self delivery','Manual delivery','Customer pickup','Other']
const PAYMENT_METHODS = ['Cash on delivery','bKash','Nagad','Rocket','Upay','Bank transfer','Cash','At counter','PayPal','Wise','PayID','Other']
const PAYMENT_STATUSES= ['Unpaid COD','Paid in full','50% advance','Partial payment']
const ORDER_SOURCES   = ['Facebook inbox','Facebook page','Facebook comment','WhatsApp','Instagram','TikTok','Phone call','Walk-in','Website','Event / Fair','Customer order form','Other']
const BROAD_SOURCES   = ['Online','Phone call','Walk-in / POS','Event']

let pdfGenerating = false

function generateReceiptPDF(order, seller, brand, orderItems = []) {
  if (pdfGenerating) return
  pdfGenerating = true
  import('jspdf').then(({ default: jsPDF }) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    let y = 20
    const center = (text, yPos, size = 10) => { doc.setFontSize(size); doc.text(text, pw / 2, yPos, { align: 'center' }) }
    const line   = (yPos) => { doc.setDrawColor(220, 220, 220); doc.line(20, yPos, pw - 20, yPos) }
    const row    = (label, value, yPos) => { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120); doc.text(label, 20, yPos); doc.setTextColor(40, 40, 40); doc.text(String(value), pw - 20, yPos, { align: 'right' }) }
    doc.setFont('helvetica', 'bold'); doc.setTextColor(24, 119, 242)
    center(brand?.name || 'PageConnect', y, 18); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center(brand?.website || 'pageconnect.io', y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(20, 20, 20)
    center(seller?.shop_name || 'Seller', y, 14); y += 6
    if (seller?.phone) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100); center(seller.phone, y, 9); y += 5 }
    y += 4; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(80, 80, 80)
    center('RECEIPT', y, 11); y += 6
    doc.setFontSize(12); doc.setTextColor(24, 119, 242); center(order.order_code, y, 12); y += 5
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center(new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80)
    doc.text(orderItems.length > 1 ? 'PRODUCTS' : 'PRODUCT', 20, y); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20, 20, 20)
    if (orderItems.length > 0) {
      orderItems.forEach((item, idx) => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(20, 20, 20)
        const itemLabel = item.product_name + (item.variant_name ? ' · ' + item.variant_name : '')
        const itemLines = doc.splitTextToSize(itemLabel, pw - 60)
        doc.text(itemLines, 20, y); y += itemLines.length * 5
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 80, 80)
        const lineBase = Number(item.unit_price || 0) * Number(item.quantity || 1)
        row('Qty: ' + item.quantity + ' x ' + Number(item.unit_price || 0).toLocaleString(), lineBase.toLocaleString(), y); y += 5
        if (Number(item.line_discount_amount || 0) > 0) { row('  Discount (' + item.line_discount_percent + '%)', '-' + Number(item.line_discount_amount).toLocaleString(), y); y += 4 }
        if (Number(item.line_vat_amount || 0) > 0) { row('  VAT (' + item.line_vat_percent + '%)', '+' + Number(item.line_vat_amount).toLocaleString(), y); y += 4 }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(20, 20, 20)
        row('  Line total', Number(item.line_subtotal || 0).toLocaleString(), y); y += idx < orderItems.length - 1 ? 6 : 4
        if (idx < orderItems.length - 1) { doc.setDrawColor(220, 220, 220); doc.line(20, y - 2, pw - 20, y - 2) }
      })
    } else {
      doc.text(order.product_name || order.product_code || 'Product', 20, y); y += 5
      doc.setFontSize(9); doc.setTextColor(80, 80, 80)
      const unitTotal = Number(order.unit_price || 0) * Number(order.quantity || 1)
      row('Qty: ' + order.quantity + ' x ' + Number(order.unit_price || 0).toLocaleString(), unitTotal.toLocaleString(), y); y += 5
    }
    if (Number(order.discount_amount || 0) > 0) { row('Order discount (' + (order.discount_percent || '') + '%)', '-' + Number(order.discount_amount).toLocaleString(), y); y += 5 }
    if (Number(order.vat_amount || 0) > 0) { row((order.tax_type || 'VAT') + ' (' + (order.vat_percent || '') + '%)', '+' + Number(order.vat_amount).toLocaleString(), y); y += 5 }
    row('Delivery charge', Number(order.delivery_charge || 0).toLocaleString(), y); y += 6; line(y); y += 6
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(20, 20, 20)
    doc.text('Total', 20, y); doc.setTextColor(24, 119, 242)
    doc.text(Number(order.total_amount || 0).toLocaleString(), pw - 20, y, { align: 'right' }); y += 10
    line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text('PAYMENT', 20, y); y += 6
    row('Method', order.payment_method || 'N/A', y); y += 5
    row('Status', order.payment_status || 'N/A', y); y += 5
    const balance = Number(order.total_amount || 0) - Number(order.amount_paid || 0)
    if (balance > 0) { doc.setTextColor(217, 119, 6); doc.text('Balance due', 20, y); doc.setFont('helvetica', 'bold'); doc.text(balance.toLocaleString(), pw - 20, y, { align: 'right' }); y += 5 }
    y += 3; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text('CUSTOMER', 20, y); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20, 20, 20)
    doc.text(order.customers?.name || 'Customer', 20, y); y += 5
    if (order.customers?.phone) { doc.setFontSize(9); doc.setTextColor(120, 120, 120); doc.text(order.customers.phone, 20, y); y += 5 }
    y += 5; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(24, 119, 242)
    center('Thank you for shopping!', y, 11); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center('with ' + (seller?.shop_name || 'the seller'), y, 9); y += 5
    center('For queries contact the seller directly.', y, 9); y += 10
    doc.setFontSize(7); doc.setTextColor(160, 160, 160)
    const disclaimer = 'This receipt is generated by ' + (brand?.name || 'PageConnect') + ' on behalf of the seller. All matters are between buyer and seller. ' + (brand?.name || 'PageConnect') + ' holds no liability.'
    const dLines = doc.splitTextToSize(disclaimer, pw - 40)
    doc.text(dLines, 20, y); y += dLines.length * 4 + 5; line(y); y += 6
    doc.setFontSize(8); doc.setTextColor(24, 119, 242)
    center('Powered by ' + (brand?.name || 'PageConnect') + ' · ' + (brand?.website || 'pageconnect.io'), y, 8)
    doc.save('receipt-' + order.order_code + '.pdf')
    pdfGenerating = false
  }).catch(() => { pdfGenerating = false })
}

function ReceiptModal({ order, seller, brand, canDownload, onClose, onUpgrade, orderItems = [], t }) {
  const balance = Number(order.total_amount || 0) - Number(order.amount_paid || 0)
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, width: '100%', maxWidth: '420px', borderRadius: '20px 20px 0 0', maxHeight: '90vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body)' }}>
        <div style={{ padding: '14px 16px', background: BLUE, borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: WHITE }}>{t('order_detail.e_receipt')}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canDownload ? (
              <button onClick={() => generateReceiptPDF(order, seller, brand, orderItems)}
                style={{ fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '8px', background: WHITE, color: BLUE, border: 'none', cursor: 'pointer' }}>
                {t('order_detail.download_pdf')}
              </button>
            ) : (
              <button onClick={onUpgrade}
                style={{ fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '8px', background: '#FCD34D', color: TEXT, border: 'none', cursor: 'pointer' }}>
                {t('order_detail.upgrade_download')}
              </button>
            )}
            <button onClick={onClose}
              style={{ fontSize: '11.5px', padding: '5px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.2)', color: WHITE, border: 'none', cursor: 'pointer' }}>
              {t('order_detail.close')}
            </button>
          </div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
          <div style={{ textAlign: 'center', borderBottom: '1px solid ' + BORDER, paddingBottom: '16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: TEXT }}>{seller?.shop_name}</div>
            {seller?.phone && <div style={{ fontSize: '12px', color: MUTED, marginTop: '3px' }}>{seller.phone}</div>}
          </div>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', marginBottom: '4px' }}>RECEIPT</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: BLUE, fontFamily: 'JetBrains Mono, monospace' }}>{order.order_code}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{new Date(order.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          <div style={{ borderTop: '1px solid ' + BORDER, paddingTop: '14px', marginBottom: '14px' }}>
            {orderItems.length > 0 ? (
              <>
                {orderItems.map((item, idx) => (
                  <div key={item.id} style={{ marginBottom: '10px', paddingBottom: idx < orderItems.length - 1 ? '10px' : 0, borderBottom: idx < orderItems.length - 1 ? '1px solid ' + BORDER : 'none' }}>
                    <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, marginBottom: '2px' }}>
                      {item.product_name}{item.variant_name ? ' · ' + item.variant_name : ''}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: MUTED, marginBottom: '2px' }}>
                      <span>{item.quantity} x {Number(item.unit_price || 0).toLocaleString()}</span>
                      <span style={{ fontWeight: 600, color: TEXT }}>{Number(item.line_subtotal || 0).toLocaleString()}</span>
                    </div>
                    {Number(item.line_discount_amount || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: MUTED }}>
                        <span>Discount ({item.line_discount_percent}%)</span>
                        <span style={{ color: '#DC2626' }}>-{Number(item.line_discount_amount).toLocaleString()}</span>
                      </div>
                    )}
                    {Number(item.line_vat_amount || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: MUTED }}>
                        <span>VAT ({item.line_vat_percent}%)</span>
                        <span>+{Number(item.line_vat_amount).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                ))}
                {orderItems.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: MUTED, marginBottom: '6px' }}>
                    <span>{t('order_detail.items_subtotal')}</span>
                    <span style={{ fontWeight: 600, color: TEXT }}>{orderItems.reduce((s, i) => s + Number(i.line_subtotal || 0), 0).toLocaleString()}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '6px' }}>{order.product_name || order.product_code}</div>
                {order.variant_name && <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '4px' }}>{t('order_detail.variant')} {order.variant_name}</div>}
                <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '8px' }}>{order.quantity} x {Number(order.unit_price || 0).toLocaleString()}</div>
              </>
            )}
            {Number(order.discount_amount || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: MUTED }}>{t('order_detail.discount')} ({order.discount_percent}%)</span>
                <span style={{ color: '#DC2626' }}>-{Number(order.discount_amount).toLocaleString()}</span>
              </div>
            )}
            {Number(order.vat_amount || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: MUTED }}>{order.tax_type || 'VAT'} ({order.vat_percent}%)</span>
                <span style={{ color: TEXT }}>{Number(order.vat_amount).toLocaleString()}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px' }}>
              <span style={{ color: MUTED }}>{t('order_detail.delivery')}</span>
              <span style={{ color: TEXT }}>{Number(order.delivery_charge || 0).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid ' + BORDER, paddingTop: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>{t('order_detail.total')}</span>
              <span style={{ fontSize: '16px', fontWeight: 700, color: BLUE }}>{Number(order.total_amount || 0).toLocaleString()}</span>
            </div>
          </div>
          <div style={{ background: BG, borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, marginBottom: '8px' }}>{t('order_detail.payment')}</div>
            {[{ label: t('order_detail.method'), value: order.payment_method }, { label: t('order_detail.status_label'), value: order.payment_status }].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: MUTED }}>{r.label}</span>
                <span style={{ fontWeight: 500, color: TEXT }}>{r.value || 'N/A'}</span>
              </div>
            ))}
            {balance > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid ' + BORDER, paddingTop: '8px', marginTop: '4px' }}>
                <span style={{ color: '#D97706', fontWeight: 600 }}>{t('order_detail.balance_due')}</span>
                <span style={{ color: '#D97706', fontWeight: 700 }}>{balance.toLocaleString()}</span>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', borderTop: '1px solid ' + BORDER, paddingTop: '14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '3px' }}>{t('order_detail.thank_you')}</div>
            <div style={{ fontSize: '12px', color: MUTED }}>{t('order_detail.with_seller')} {seller?.shop_name}</div>
          </div>
          <div style={{ background: BG, borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: MUTED, lineHeight: 1.6 }}>
              {t('order_detail.receipt_disclaimer').replace('{brand}', brand?.name || 'PageConnect')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: MUTED }}>{t('order_detail.powered_by')}</div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: BLUE }}>{brand?.name || 'PageConnect'}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InfoRow({ label, value, accent, mono }) {
  if (!value || value === 'N/A' || value === '' || value === '0') return null
  return (
    <div>
      <div style={{ fontSize: '11px', color: MUTED, marginBottom: '3px' }}>{label}</div>
      <div style={{ fontSize: '12.5px', fontWeight: 600, color: accent || TEXT, fontFamily: mono ? 'JetBrains Mono, monospace' : 'var(--font-body)' }}>
        {value}
      </div>
    </div>
  )
}

function Icon({ path, size = 16, color = MUTED, strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  back:   'M15 18l-6-6 6-6',
  receipt:'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  status: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  edit:   'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  copy:   'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  trash:  'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  check:  'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  phone:  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  inbox:  'M3 8l9 6 9-6M3 8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z',
  clock:  'M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2',
  checkCircle: 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
  gear:   'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82A1.65 1.65 0 003 14.09H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  truck:  'M1 3h14v13H1zM15 8h4l3 3v5h-7V8z M5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  mailOpen: 'M3 8l9 6 9-6M3 8a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z',
  lock:   'M19 11H5a2 2 0 00-2 2v6a2 2 0 002 2h14a2 2 0 002-2v-6a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',
  undo:   'M3 7v6h6 M21 17a9 9 0 00-15-6.7L3 13',
  refreshLg: 'M21 2v6h-6 M3 22v-6h6 M3.51 9a9 9 0 0114.85-3.36L21 8 M20.49 15a9 9 0 01-14.85 3.36L3 16',
  clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
}

function StatusIcon({ status, size = 13, color = MUTED, strokeWidth = 2 }) {
  return <Icon path={IC[STATUS_ICON_KEYS[status]] || IC.clipboard} size={size} color={color} strokeWidth={strokeWidth} />
}

function OrderDetail() {
  const { id }     = useParams()
  const navigate   = useNavigate()
  const { seller, isFeatureAllowed } = useAuth()
  const { brand }  = useBrand()
  const { showToast } = useToast()
  const { t }      = useTranslation()

  const [order, setOrder]                   = useState(null)
  const [product, setProduct]               = useState(null)
  const [statusLogs, setStatusLogs]         = useState([])
  const [dashOrders, setDashOrders]         = useState([])
  const [loading, setLoading]               = useState(true)
  const [showStatusSheet, setShowStatusSheet]     = useState(false)
  const [showRestockPrompt, setShowRestockPrompt] = useState(false)
  const [showDashPrompt, setShowDashPrompt]       = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showReceipt, setShowReceipt]       = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [editMode, setEditMode]             = useState(false)
  const [newStatus, setNewStatus]           = useState('')
  const [statusNote, setStatusNote]         = useState('')
  const [saving, setSaving]                 = useState(false)
  const [deleting, setDeleting]             = useState(false)
  const [creatingDash, setCreatingDash]     = useState(false)
  const [copied, setCopied]                 = useState(false)
  const [savingEdit, setSavingEdit]         = useState(false)
  const [editForm, setEditForm]             = useState({})
  const [orderItems, setOrderItems]         = useState([])

  const canDownloadReceipt = isFeatureAllowed('receipts')

  const fetchOrder = async () => {
    setLoading(true)
    const { data } = await supabase.from('orders').select('*, customers (*)').eq('id', id).single()
    if (data) {
      setOrder(data)
      setNewStatus(data.status)
      setEditForm({
        name: data.customers?.name || '', phone: data.customers?.phone || '',
        house: data.customers?.house || '', road: data.customers?.road || '',
        moholla: data.customers?.moholla || '', thana: data.customers?.thana || '',
        district: data.customers?.district || '', division: data.customers?.division || '',
        zip: data.customers?.zip || '', quantity: data.quantity || 1,
        unit_price: data.unit_price || '', delivery_charge: data.delivery_charge || 0,
        courier: data.courier || 'Pathao', payment_method: data.payment_method || 'Cash on delivery',
        payment_status: data.payment_status || 'Unpaid COD', amount_paid: data.amount_paid || '',
        tracking_number: data.tracking_number || '', expected_delivery: data.expected_delivery || '',
        taken_by: data.taken_by || '', notes: data.notes || '',
        order_source: data.order_source || '', broad_source: data.broad_source || '',
        b2b_reference: data.b2b_reference || '',
      })
      if (data.product_id) {
        const { data: prod } = await supabase.from('products').select('name, product_type, color, image_url').eq('id', data.product_id).single()
        setProduct(prod)
        if (!data.product_name && prod?.name) await supabase.from('orders').update({ product_name: prod.name }).eq('id', id)
      }
      const { data: logs } = await supabase.from('order_status_log').select('*').eq('order_id', id).order('created_at', { ascending: false })
      setStatusLogs(logs || [])
      const { data: dashes } = await supabase.from('orders').select('*').eq('parent_order_id', id).order('dash_number', { ascending: true })
      setDashOrders(dashes || [])
      const { data: items } = await supabase.from('order_items').select('*').eq('order_id', id).order('created_at', { ascending: true })
      setOrderItems(items || [])
    }
    setLoading(false)
  }

  useEffect(() => { fetchOrder() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const logActivity = async (action, oldValue = null, newValue = null, extraData = null) => {
    await supabase.from('activity_logs').insert({ entity_type: 'order', entity_id: id, action, old_value: oldValue, new_value: newValue, done_by: 'Owner', seller_id: seller?.id, extra_data: extraData })
  }

  const reinstateStock = async (orderData) => {
    const o = orderData || order
    if (!o.product_id) return
    if (o.variant_id) {
      const { data: variant } = await supabase.from('product_variants').select('stock').eq('id', o.variant_id).single()
      if (variant) {
        await supabase.from('product_variants').update({ stock: (variant.stock || 0) + (o.quantity || 1) }).eq('id', o.variant_id)
        const { data: allVariants } = await supabase.from('product_variants').select('stock').eq('product_id', o.product_id)
        const totalStock = allVariants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0
        await supabase.from('products').update({ stock: totalStock }).eq('id', o.product_id)
      }
    } else {
      const { data: prod } = await supabase.from('products').select('id, stock').eq('id', o.product_id).single()
      if (prod) await supabase.from('products').update({ stock: (prod.stock || 0) + (o.quantity || 1) }).eq('id', prod.id)
    }
    await supabase.from('stock_log').insert({ product_id: o.product_id, action: 'restock', quantity: o.quantity || 1, note: 'Order ' + o.order_code + ' deleted — stock reinstated', done_by: 'Owner' })
  }

  const deductStock = async () => {
    if (!order.product_id) return
    if (order.variant_id) {
      const { data: variant } = await supabase.from('product_variants').select('stock').eq('id', order.variant_id).single()
      if (variant) {
        const newStock = Math.max(0, (variant.stock || 0) - (order.quantity || 1))
        await supabase.from('product_variants').update({ stock: newStock }).eq('id', order.variant_id)
        const { data: allVariants } = await supabase.from('product_variants').select('stock').eq('product_id', order.product_id)
        const totalStock = allVariants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0
        await supabase.from('products').update({ stock: totalStock }).eq('id', order.product_id)
      }
    } else {
      const { data: prod } = await supabase.from('products').select('id, stock').eq('id', order.product_id).single()
      if (prod) await supabase.from('products').update({ stock: Math.max(0, (prod.stock || 0) - (order.quantity || 1)) }).eq('id', prod.id)
    }
    await supabase.from('stock_log').insert({ product_id: order.product_id, action: 'deducted', quantity: order.quantity || 1, note: 'Order ' + order.order_code + ' confirmed', done_by: 'System' })
  }

  const handleDeleteOrder = async () => {
    setDeleting(true)
    if (STOCK_DEDUCTED_STATUSES.includes(order.status)) await reinstateStock(order)
    await logActivity('order_deleted', order.status, null, { order_code: order.order_code, customer: order.customers?.name, total: order.total_amount, product: order.product_name })
    await supabase.from('order_status_log').delete().eq('order_id', id)
    await supabase.from('orders').delete().eq('id', id)
    setDeleting(false)
    showToast(t('order_detail.order_deleted'))
    navigate('/orders')
  }

  const handleSaveEdit = async () => {
    setSavingEdit(true)
    const oldSnapshot = JSON.stringify({ quantity: order.quantity, unit_price: order.unit_price, delivery_charge: order.delivery_charge, payment_status: order.payment_status, order_source: order.order_source, broad_source: order.broad_source, b2b_reference: order.b2b_reference })
    const newTotal = (Number(editForm.unit_price) * Number(editForm.quantity)) + Number(editForm.delivery_charge)
    await supabase.from('orders').update({
      quantity: editForm.quantity, unit_price: editForm.unit_price,
      delivery_charge: editForm.delivery_charge, total_amount: newTotal,
      courier: editForm.courier, payment_method: editForm.payment_method,
      payment_status: editForm.payment_status, amount_paid: editForm.amount_paid || 0,
      tracking_number: editForm.tracking_number || null, expected_delivery: editForm.expected_delivery || null,
      taken_by: editForm.taken_by || null, notes: editForm.notes,
      order_source: editForm.order_source, broad_source: editForm.broad_source || null,
      b2b_reference: editForm.b2b_reference || null,
    }).eq('id', id)
    await supabase.from('customers').update({
      name: editForm.name, phone: editForm.phone, house: editForm.house, road: editForm.road,
      moholla: editForm.moholla, thana: editForm.thana, district: editForm.district,
      division: editForm.division, zip: editForm.zip,
    }).eq('id', order.customer_id)
    await logActivity('order_edited', oldSnapshot, JSON.stringify({ quantity: editForm.quantity, unit_price: editForm.unit_price, delivery_charge: editForm.delivery_charge, payment_status: editForm.payment_status, order_source: editForm.order_source }), { fields_updated: Object.keys(editForm) })
    await fetchOrder()
    setSavingEdit(false)
    setEditMode(false)
    showToast(t('order_detail.order_updated'))
  }

  const handleStatusChange = async () => {
    if (!newStatus || newStatus === order.status) return
    setSaving(true)
    const oldStatus = order.status
    await supabase.from('orders').update({ status: newStatus }).eq('id', id)
    await supabase.from('order_status_log').insert({ order_id: id, old_status: oldStatus, new_status: newStatus, note: statusNote, changed_by: 'Owner' })
    await logActivity('status_changed', oldStatus, newStatus)
    if (newStatus === 'confirmed' && oldStatus !== 'confirmed') await deductStock()
    if (newStatus === 'return') {
      setSaving(false)
      setShowStatusSheet(false)
      setStatusNote('')
      setShowReturnModal(true)
      return
    }
    await fetchOrder()
    setShowStatusSheet(false)
    setStatusNote('')
    setSaving(false)
    showToast(t('order_detail.status_updated') + ' ' + STATUSES.find(s => s.key === newStatus)?.label)
  }

  const handleReturnConfirm = async ({ returnReasonCategory, returnReasonDetail, isSellerFault }) => {
    // Save return reason to order
    await supabase.from('orders').update({
      return_reason_category: returnReasonCategory,
      return_reason_detail: returnReasonDetail || null,
      return_is_seller_fault: isSellerFault,
    }).eq('id', id)

    await logActivity('return_reason_set', null, returnReasonCategory, {
      return_reason: returnReasonCategory,
      seller_fault: isSellerFault,
      detail: returnReasonDetail,
    })

    // Auto-flag customer for customer-side returns
    if (!isSellerFault && order.customers?.phone) {
      try {
        await supabase.from('customer_flags').insert({
          phone: order.customers.phone,
          flagged_by_seller_id: seller.id,
          reason_category: 'customer_return',
          reason_detail: returnReasonCategory?.replace(/_/g, ' ') + (returnReasonDetail ? ' — ' + returnReasonDetail : ''),
          order_id: id,
          is_private: true,
          is_active: true,
        })
      } catch (e) {
        // Flag may already exist — ignore duplicate
      }
    }

    setShowReturnModal(false)
    await fetchOrder()
    setShowRestockPrompt(true)
  }

  const handleRestockReturn = async () => {
    if (!order.product_id) return
    await reinstateStock(order)
    setShowRestockPrompt(false)
    setShowDashPrompt(true)
    await fetchOrder()
    showToast(t('order_detail.stock_reinstated'))
  }

  const createDashOrder = async () => {
    setCreatingDash(true)
    const dashNumber = dashOrders.length + 1
    const dashCode   = order.order_code + '-D' + dashNumber
    await supabase.from('orders').insert({
      seller_id: seller?.id, order_code: dashCode, customer_id: order.customer_id,
      product_id: order.product_id, product_code: order.product_code,
      product_name: order.product_name, variant_id: order.variant_id,
      variant_name: order.variant_name, quantity: order.quantity, unit_price: order.unit_price,
      delivery_charge: order.delivery_charge, total_amount: order.total_amount,
      order_source: order.order_source, courier: order.courier,
      payment_method: order.payment_method, payment_status: order.payment_status,
      notes: 'Dash order from ' + order.order_code, status: 'received',
      parent_order_id: order.id, is_dash_order: true, dash_number: dashNumber,
    })
    await logActivity('dash_order_created', order.order_code, dashCode)
    setCreatingDash(false)
    setShowDashPrompt(false)
    await fetchOrder()
    showToast(t('order_detail.dash_created') + ' ' + dashCode)
  }

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(addressPreview)
    setCopied(true)
    showToast(t('order_detail.address_copied'))
    setTimeout(() => setCopied(false), 2000)
  }

  const currentStatusObj   = STATUSES.find(s => s.key === order?.status)
  const stockWasDeducted   = STOCK_DEDUCTED_STATUSES.includes(order?.status)
  // Only show the "Pre-order" step for orders that actually were one — avoids every
  // regular order showing a falsely "completed" Pre-order step at the start of the pipeline
  const pipelineStatuses    = STATUSES.filter(s => s.key !== 'return' && (order?.is_preorder || s.key !== 'pre_order'))
  const pipelineCurrentIdx  = pipelineStatuses.findIndex(s => s.key === order?.status)
  const fmt     = (n) => Number(n || 0).toLocaleString()
  const balance = Number(order?.total_amount || 0) - Number(order?.amount_paid || 0)
  const hasVat  = Number(order?.vat_amount || 0) > 0

  const addressPreview = order ? [
    order.customers?.house && order.customers.house !== 'N/A' ? 'House: ' + order.customers.house : null,
    order.customers?.road && order.customers.road !== 'N/A' ? 'Road: ' + order.customers.road : null,
    order.customers?.moholla && order.customers.moholla !== 'N/A' ? order.customers.moholla : null,
    order.customers?.neighbourhood && order.customers.neighbourhood !== 'N/A' ? order.customers.neighbourhood : null,
    order.customers?.metro_thana && order.customers.metro_thana !== 'N/A' ? order.customers.metro_thana : null,
    order.customers?.union_name && order.customers.union_name !== 'N/A' ? order.customers.union_name : null,
    order.customers?.thana && order.customers.thana !== 'N/A' ? order.customers.thana : null,
    order.customers?.zip && order.customers.zip !== 'N/A' ? order.customers.zip : null,
    order.customers?.district && order.customers.district !== 'N/A' ? order.customers.district : null,
    order.customers?.division && order.customers.division !== 'N/A' ? order.customers.division : null,
    order.customers?.country && order.customers.country !== 'N/A' ? order.customers.country : null,
  ].filter(Boolean).join(', ') : ''

  const card = (children, extraStyle = {}) => (
    <div className="pc-fade-up" style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, boxShadow: 'var(--shadow-sm)', padding: '18px', ...extraStyle }}>
      {children}
    </div>
  )

  const sectionLabel = (text) => (
    <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>
      {text}
    </div>
  )

  const inputStyle = { width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', background: BG, color: TEXT, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, flexDirection: 'column', gap: '12px' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2.5px solid ' + BLUE, borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
      <div style={{ fontSize: '13px', color: MUTED }}>{t('order_detail.loading')}</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  if (!order) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG }}>
      <div style={{ fontSize: '13px', color: MUTED }}>{t('order_detail.not_found')}</div>
    </div>
  )

  return (
    <div className="flex-1 overflow-x-hidden" style={{ background: BG, fontFamily: 'var(--font-body)' }}>
      <Topbar title={order.is_dash_order ? t('order_detail.dash_title') : t('order_detail.title')} />

      <div style={{ padding: '18px 24px', maxWidth: '1100px' }}>

        {/* Header card */}
        {card(
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{order.order_code}</div>
                  {order.is_dash_order && (
                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: '#FFF8E6', color: '#D97706' }}>
                      Dash D{order.dash_number}
                    </span>
                  )}
                  {currentStatusObj && (
                    <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: 'var(--radius-full)', background: currentStatusObj.bg, color: currentStatusObj.text }}>
                      {currentStatusObj.label}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: MUTED }}>
                    {new Date(order.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' · '}
                    {new Date(order.created_at).toLocaleTimeString('en-BD', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {order.submitted_by && <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '6px', background: 'var(--primary-light)', color: BLUE }}>{order.submitted_by}</span>}
                  {order.broad_source && <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '6px', background: BG, color: MUTED }}>{order.broad_source}</span>}
                </div>
              </div>
              <button onClick={() => setEditMode(!editMode)}
                style={{ fontSize: '12.5px', fontWeight: 600, padding: '7px 16px', borderRadius: '9px', border: '1px solid ' + BORDER, background: editMode ? '#FEF2F2' : WHITE, color: editMode ? '#DC2626' : TEXT, cursor: 'pointer' }}>
                {editMode ? t('order_detail.cancel_edit') : t('order_detail.edit_order')}
              </button>
            </div>

            {order.is_dash_order && order.parent_order_id && (
              <button onClick={() => navigate('/orders/' + order.parent_order_id)}
                style={{ width: '100%', textAlign: 'left', fontSize: '12px', padding: '8px 12px', borderRadius: '9px', marginBottom: '14px', border: '1px solid #FDE68A', background: '#FFFBEB', color: '#92400E', cursor: 'pointer' }}>
                {t('order_detail.view_parent')} {order.order_code.split('-D')[0]}
              </button>
            )}

            {/* Return reason badge — shown if return reason was recorded */}
            {order.status === 'return' && order.return_reason_category && (
              <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: '10px', background: order.return_is_seller_fault ? '#EFF6FF' : '#FEF2F2', border: `1px solid ${order.return_is_seller_fault ? '#BFDBFE' : '#FECACA'}` }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: order.return_is_seller_fault ? BLUE : '#DC2626', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>
                  Return Reason — {order.return_is_seller_fault ? 'Our Side' : 'Customer Side'}
                </div>
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>
                  {order.return_reason_category?.replace(/_/g, ' ')}
                </div>
                {order.return_reason_detail && (
                  <div style={{ fontSize: '12px', color: MUTED, marginTop: '3px' }}>{order.return_reason_detail}</div>
                )}
              </div>
            )}

            {/* Status pipeline */}
            <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '4px' }}>
              {pipelineStatuses.map((s, i, arr) => {
                const isDone    = i < pipelineCurrentIdx
                const isCurrent = s.key === order.status
                return (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, background: isDone ? BLUE : isCurrent ? TEXT : BORDER, color: isDone || isCurrent ? WHITE : MUTED, boxShadow: isCurrent ? '0 0 0 3px var(--primary-border)' : 'none', transition: 'all 0.2s' }}>
                        {isDone ? '✓' : i + 1}
                      </div>
                      <div style={{ fontSize: '9.5px', marginTop: '4px', whiteSpace: 'nowrap', color: isCurrent ? TEXT : isDone ? BLUE : MUTED, fontWeight: isCurrent ? 700 : 400 }}>
                        {s.label}
                      </div>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ width: '28px', height: '2px', margin: '0 4px 14px', borderRadius: '2px', background: i < pipelineCurrentIdx ? BLUE : BORDER }} />
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* Edit form */}
        {editMode && (
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginTop: '12px' }}>
            <div style={{ padding: '12px 18px', background: BLUE, borderBottom: '1px solid ' + BORDER }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: WHITE }}>{t('order_detail.edit_order')}</div>
            </div>
            <div style={{ padding: '20px 18px' }}>
              <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>{t('order_detail.customer_details')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }} className="edit-grid">
                {[{ key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' }].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{f.label}</div>
                    <input style={inputStyle} value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      onFocus={e => e.target.style.borderColor = BLUE}
                      onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>
                ))}
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>House / Flat</div>
                  <input style={inputStyle} value={editForm.house || ''}
                    onChange={e => setEditForm(p => ({ ...p, house: e.target.value }))}
                    onFocus={e => e.target.style.borderColor = BLUE}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                {[{ key: 'road', label: 'Road' }, { key: 'moholla', label: 'Moholla' }, { key: 'thana', label: 'Thana' }, { key: 'district', label: 'District' }, { key: 'zip', label: 'ZIP' }].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{f.label}</div>
                    <input style={inputStyle} value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      onFocus={e => e.target.style.borderColor = BLUE}
                      onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>
                ))}
                <div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>Division</div>
                  <select style={inputStyle} value={editForm.division || ''} onChange={e => setEditForm(p => ({ ...p, division: e.target.value }))}>
                    <option value="">Select</option>
                    {DIVISIONS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>{t('order_detail.order_details_edit')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }} className="edit-grid">
                {[
                  { key: 'quantity', label: t('order_detail.product'), type: 'number' },
                  { key: 'unit_price', label: 'Unit price', type: 'number' },
                  { key: 'delivery_charge', label: t('order_detail.delivery_charge'), type: 'number' },
                  { key: 'amount_paid', label: t('order_detail.amount_paid'), type: 'number' },
                  { key: 'tracking_number', label: t('order_detail.tracking_number'), type: 'text' },
                  { key: 'taken_by', label: t('order_detail.taken_by'), type: 'text' },
                  { key: 'expected_delivery', label: t('order_detail.expected_delivery'), type: 'date' },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{f.label}</div>
                    <input type={f.type} style={inputStyle} value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      onFocus={e => e.target.style.borderColor = BLUE}
                      onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>
                ))}
                {[
                  { key: 'order_source', label: t('order_detail.order_source_detail'), options: ORDER_SOURCES },
                  { key: 'broad_source', label: t('order_detail.broad_source'), options: BROAD_SOURCES },
                  { key: 'courier', label: t('order_detail.courier'), options: COURIERS },
                  { key: 'payment_method', label: t('order_detail.payment_method'), options: PAYMENT_METHODS },
                  { key: 'payment_status', label: t('order_detail.payment_status'), options: PAYMENT_STATUSES },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{f.label}</div>
                    <select style={inputStyle} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}>
                      <option value="">Select</option>
                      {f.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{t('order_detail.b2b_reference')} (BIN / Trade licence / ABN / Tax ID)</div>
                  <input style={inputStyle} value={editForm.b2b_reference || ''} placeholder="Optional — for B2B tax claims"
                    onChange={e => setEditForm(p => ({ ...p, b2b_reference: e.target.value }))}
                    onFocus={e => e.target.style.borderColor = BLUE}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px' }}>{t('order_detail.notes')}</div>
                  <textarea style={{ ...inputStyle, resize: 'none' }} rows={2} value={editForm.notes || ''}
                    onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                    onFocus={e => e.target.style.borderColor = BLUE}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
              </div>

              <div style={{ borderRadius: '10px', padding: '12px 16px', background: 'var(--primary-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '12.5px', color: BLUE, fontWeight: 600 }}>{t('order_detail.new_total')}</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: BLUE }}>
                  {((Number(editForm.unit_price) * Number(editForm.quantity)) + Number(editForm.delivery_charge)).toLocaleString()}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowDeleteConfirm(true)}
                  style={{ fontSize: '12.5px', fontWeight: 600, padding: '10px 16px', borderRadius: '9px', background: '#FEF2F2', color: '#DC2626', border: 'none', cursor: 'pointer' }}>
                  {t('order_detail.delete_order')}
                </button>
                <button onClick={handleSaveEdit} disabled={savingEdit}
                  style={{ flex: 1, fontSize: '13px', fontWeight: 600, padding: '10px', borderRadius: '9px', background: BLUE, color: WHITE, border: 'none', cursor: 'pointer', opacity: savingEdit ? 0.6 : 1 }}>
                  {savingEdit ? t('order_detail.saving') : t('order_detail.save_changes')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main 2-col grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }} className="detail-grid">

          {/* Products */}
          {card(
            <>
              {sectionLabel(orderItems.length > 1 ? t('order_detail.products') + ' (' + orderItems.length + ')' : t('order_detail.product'))}
              {orderItems.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {orderItems.map((item, idx) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', paddingBottom: idx < orderItems.length - 1 ? '10px' : 0, borderBottom: idx < orderItems.length - 1 ? '1px solid ' + BORDER : 'none' }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '11px', fontWeight: 700, color: MUTED }}>
                        {idx + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: TEXT, marginBottom: '3px' }}>{item.product_name}</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '5px' }}>
                          {item.variant_name && <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: '#6D28D9' }}>{item.variant_name}</span>}
                          {item.product_code && <span style={{ fontSize: '11px', color: BLUE, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{item.product_code}</span>}
                        </div>
                        <div style={{ fontSize: '12.5px', color: MUTED }}>
                          {fmt(item.unit_price)} &#215; {item.quantity}
                          {item.line_discount_amount > 0 && <span style={{ color: '#DC2626' }}> &#8722; {fmt(item.line_discount_amount)} {t('order_detail.disc')}</span>}
                          {item.line_vat_amount > 0 && <span> + {fmt(item.line_vat_amount)} VAT</span>}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginTop: '3px' }}>{fmt(item.line_subtotal)}</div>
                      </div>
                    </div>
                  ))}
                  {orderItems.length > 1 && (
                    <div style={{ borderTop: '1px solid ' + BORDER, paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: MUTED }}>{t('order_detail.items_subtotal')}</span>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>{fmt(orderItems.reduce((s, i) => s + Number(i.line_subtotal || 0), 0))}</span>
                    </div>
                  )}
                </div>
              ) : (product || order.product_name) ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                    {product?.image_url
                      ? <img src={product.image_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" color={BLUE} size={20} strokeWidth={1.5} />
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: TEXT, marginBottom: '3px' }}>{product?.name || order.product_name}</div>
                    {product?.product_type && <div style={{ fontSize: '12px', color: MUTED, marginBottom: '5px' }}>{product.product_type}</div>}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '5px' }}>
                      {order.variant_name && <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: '#6D28D9' }}>{order.variant_name}</span>}
                      {order.product_code && <span style={{ fontSize: '11px', color: BLUE, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{order.product_code}</span>}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{fmt(order.unit_price)} &#215; {order.quantity} = {fmt(Number(order.unit_price) * Number(order.quantity))}</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: MUTED }}>{t('order_detail.no_product')}</div>
              )}
            </>
          )}

          {/* Customer */}
          {card(
            <>
              {sectionLabel(t('order_detail.customer'))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: BLUE, flexShrink: 0 }}>
                  {order.customers?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>{order.customers?.name}</div>
                  <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>{order.customers?.phone}</div>
                </div>
                <a href={'tel:' + order.customers?.phone}
                  style={{ fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '9px', background: 'var(--primary-light)', color: BLUE, textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Icon path={IC.phone} color={BLUE} size={13} strokeWidth={2} />
                  {t('order_detail.call')}
                </a>
              </div>
              {addressPreview && (
                <div style={{ borderRadius: '10px', padding: '10px 12px', background: BG, fontSize: '12px', color: TEXT, lineHeight: 1.7, marginBottom: '10px' }}>
                  {addressPreview}
                </div>
              )}
              <button onClick={handleCopyAddress}
                style={{ fontSize: '12px', fontWeight: 600, padding: '7px 14px', borderRadius: '9px', border: '1px solid ' + (copied ? BLUE : BORDER), background: copied ? 'var(--primary-light)' : WHITE, color: copied ? BLUE : TEXT, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon path={IC.copy} color={copied ? BLUE : MUTED} size={13} strokeWidth={2} />
                {copied ? t('order_detail.copied') : t('order_detail.copy_address')}
              </button>
            </>
          )}
        </div>

        {/* Payment & Delivery */}
        <div style={{ marginTop: '12px' }}>
          {card(
            <>
              {sectionLabel(t('order_detail.payment_delivery'))}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }} className="detail-info-grid">
                <InfoRow label={t('order_detail.total_amount')} value={fmt(order.total_amount)} accent={BLUE} />
                <InfoRow label={t('order_detail.delivery_charge')} value={fmt(order.delivery_charge)} />
                {Number(order.discount_amount || 0) > 0 && <InfoRow label={t('order_detail.discount') + ' (' + order.discount_percent + '%)'} value={'-' + fmt(order.discount_amount)} />}
                <InfoRow label={t('order_detail.payment_method')} value={order.payment_method} />
                <InfoRow label={t('order_detail.payment_status')} value={order.payment_status} />
                <InfoRow label={t('order_detail.amount_paid')} value={order.amount_paid ? fmt(order.amount_paid) : null} />
                {balance > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '3px' }}>{t('order_detail.balance_due')}</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#DC2626' }}>{fmt(balance)}</div>
                  </div>
                )}
                <InfoRow label={t('order_detail.courier')} value={order.courier} />
                <InfoRow label={t('order_detail.tracking_number')} value={order.tracking_number} mono />
                <InfoRow label={t('order_detail.expected_delivery')} value={order.expected_delivery} />
                <InfoRow label={t('order_detail.order_source_detail')} value={order.order_source} />
                <InfoRow label={t('order_detail.broad_source')} value={order.broad_source} />
                <InfoRow label={t('order_detail.submitted_by')} value={order.submitted_by} />
                <InfoRow label={t('order_detail.taken_by')} value={order.taken_by} />
                {order.b2b_reference && (
                  <div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '3px' }}>{t('order_detail.b2b_reference')}</div>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{order.b2b_reference}</div>
                  </div>
                )}
                {order.notes && (
                  <div style={{ gridColumn: 'span 3' }}>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>{t('order_detail.notes')}</div>
                    <div style={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.6 }}>{order.notes}</div>
                  </div>
                )}
              </div>

              {hasVat && (
                <div style={{ marginTop: '16px', borderTop: '1px solid ' + BORDER, paddingTop: '14px' }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>{t('order_detail.tax_breakdown')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <InfoRow label={t('order_detail.tax_type')} value={order.tax_type || 'VAT'} />
                    <InfoRow label={t('order_detail.rate')} value={order.vat_percent ? order.vat_percent + '%' : null} />
                    <InfoRow label={t('order_detail.tax_amount')} value={fmt(order.vat_amount)} accent="#D97706" />
                    {order.tax_challan && <InfoRow label={t('order_detail.challan')} value={order.tax_challan} mono />}
                    {order.b2b_reference && <InfoRow label={t('order_detail.business_ref')} value={order.b2b_reference} mono />}
                    <div style={{ gridColumn: 'span 3' }}>
                      <div style={{ fontSize: '11.5px', color: MUTED, padding: '8px 12px', background: '#FFF8E6', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                        {order.b2b_reference
                          ? t('order_detail.tax_note_with_ref').replace('{amount}', fmt(order.vat_amount)).replace('{total}', fmt(order.total_amount))
                          : t('order_detail.tax_note_no_ref').replace('{amount}', fmt(order.vat_amount)).replace('{total}', fmt(order.total_amount))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Dash orders */}
        {dashOrders.length > 0 && (
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginTop: '12px' }}>
            <div style={{ padding: '12px 18px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('order_detail.dash_orders')}</div>
            </div>
            {dashOrders.map(d => {
              const ds = STATUSES.find(s => s.key === d.status)
              return (
                <div key={d.id} onClick={() => navigate('/orders/' + d.id)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 18px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{d.order_code}</div>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{new Date(d.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</div>
                  </div>
                  {ds && <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: 'var(--radius-full)', background: ds.bg, color: ds.text }}>{ds.label}</span>}
                </div>
              )
            })}
          </div>
        )}

        {/* Timeline */}
        {statusLogs.length > 0 && (
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginTop: '12px' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, background: BG }}>
              <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{t('order_detail.order_timeline')}</div>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon path="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" color={BLUE} size={14} strokeWidth={2} />
                </div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{t('order_detail.order_created')}</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>
                    {new Date(order.created_at).toLocaleString()}
                    {order.taken_by && ' · by ' + order.taken_by}
                  </div>
                </div>
              </div>
              {[...statusLogs].reverse().map(log => {
                const ls = STATUSES.find(s => s.key === log.new_status)
                return (
                  <div key={log.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <StatusIcon status={log.new_status} size={14} color={ls ? ls.text : MUTED} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {t('order_detail.status_changed_to')}
                        {ls && <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: ls.bg, color: ls.text }}>{ls.label}</span>}
                      </div>
                      <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{new Date(log.created_at).toLocaleString()} · {log.changed_by}</div>
                      {log.note && (
                        <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '5px', padding: '6px 10px', borderRadius: '8px', background: BG, fontStyle: 'italic' }}>
                          &ldquo;{log.note}&rdquo;
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="od-actions" style={{ display: 'flex', gap: '10px', paddingBottom: '40px', marginTop: '16px' }}>
          <button onClick={() => navigate('/orders')}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <Icon path={IC.back} color={MUTED} size={14} strokeWidth={2} />
            {t('order_detail.back')}
          </button>
          <button onClick={() => setShowReceipt(true)}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <Icon path={IC.receipt} color={TEXT} size={14} strokeWidth={2} />
            {t('order_detail.receipt')}
          </button>
          <button onClick={() => setShowStatusSheet(true)}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <Icon path={IC.status} color={WHITE} size={14} strokeWidth={2} />
            {t('order_detail.change_status')}
          </button>
        </div>
      </div>

      {/* Status sheet */}
      {showStatusSheet && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: WHITE, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '520px', padding: '20px', fontFamily: 'var(--font-body)' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>{t('order_detail.change_status_title')}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>
              {newStatus === 'confirmed' && order.status !== 'confirmed' ? t('order_detail.status_hint_confirm')
                : newStatus === 'return' ? 'You will be asked to provide a return reason.'
                : t('order_detail.status_hint_default')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
              {STATUSES.map(s => (
                <button key={s.key} onClick={() => setNewStatus(s.key)}
                  style={{ padding: '10px 12px', borderRadius: '10px', fontSize: '12.5px', fontWeight: 600, border: '1px solid ' + (newStatus === s.key ? s.text : BORDER), background: newStatus === s.key ? s.bg : WHITE, color: newStatus === s.key ? s.text : MUTED, cursor: 'pointer', transition: 'all 0.12s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <StatusIcon status={s.key} size={13} color={newStatus === s.key ? s.text : MUTED} />
                  {s.key === order.status ? s.label + ' ' + t('order_detail.current') : s.label}
                </button>
              ))}
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px' }}>{t('order_detail.note_optional')}</div>
              <input type="text" value={statusNote} onChange={e => setStatusNote(e.target.value)}
                placeholder={t('order_detail.note_placeholder')}
                style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: BG, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = BLUE}
                onBlur={e => e.target.style.borderColor = BORDER} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowStatusSheet(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                {t('order_detail.cancel')}
              </button>
              <button onClick={handleStatusChange} disabled={saving || newStatus === order.status}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer', opacity: (saving || newStatus === order.status) ? 0.5 : 1 }}>
                {saving ? t('order_detail.saving') : t('order_detail.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-body)' }}>
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <Icon path={IC.trash} color="#DC2626" size={36} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, textAlign: 'center', marginBottom: '6px' }}>{t('order_detail.delete_confirm_title')}</div>
            {stockWasDeducted && (
              <div style={{ borderRadius: '10px', padding: '10px 12px', marginBottom: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', fontSize: '12px', textAlign: 'center' }}>
                {t('order_detail.delete_stock_warning')}
              </div>
            )}
            <div style={{ fontSize: '12px', color: MUTED, textAlign: 'center', marginBottom: '20px' }}>{t('order_detail.delete_undone')}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowDeleteConfirm(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                {t('order_detail.cancel')}
              </button>
              <button onClick={handleDeleteOrder} disabled={deleting}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: '#DC2626', border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>
                {deleting ? t('order_detail.deleting') : t('order_detail.delete_order')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restock prompt */}
      {showRestockPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-body)', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <Icon path={IC.undo} color={BLUE} size={24} strokeWidth={2} />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, marginBottom: '6px' }}>{t('order_detail.return_title')}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '20px' }}>{t('order_detail.return_desc')}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowRestockPrompt(false); setShowDashPrompt(true) }}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                {t('order_detail.write_off')}
              </button>
              <button onClick={handleRestockReturn}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: '#16A34A', border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer' }}>
                {t('order_detail.yes_restock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dash prompt */}
      {showDashPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-body)', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <Icon path={IC.refreshLg} color={BLUE} size={24} strokeWidth={2} />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>{t('order_detail.dash_title_modal')}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px' }}>{t('order_detail.dash_desc')}</div>
            <div style={{ borderRadius: '10px', padding: '8px 16px', background: BG, marginBottom: '20px', display: 'inline-block' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{order.order_code}-D{dashOrders.length + 1}</div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowDashPrompt(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                {t('order_detail.no_thanks')}
              </button>
              <button onClick={createDashOrder} disabled={creatingDash}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer', opacity: creatingDash ? 0.6 : 1 }}>
                {creatingDash ? t('order_detail.creating_dash') : t('order_detail.create_dash')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Reason Modal */}
      {showReturnModal && (
        <ReturnReasonModal
          orderCode={order.order_code}
          onConfirm={handleReturnConfirm}
          onClose={() => setShowReturnModal(false)}
        />
      )}

      {showReceipt && (
        <ReceiptModal order={order} seller={seller} brand={brand}
          canDownload={canDownloadReceipt} orderItems={orderItems}
          onClose={() => setShowReceipt(false)}
          onUpgrade={() => { setShowReceipt(false); navigate('/pricing') }}
          t={t}
        />
      )}

      <style>{`
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
        .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }

        @media(max-width:860px){
          .detail-grid{grid-template-columns:1fr !important}
          .detail-info-grid{grid-template-columns:1fr 1fr !important}
          .edit-grid{grid-template-columns:1fr !important}
        }
        @media(max-width:540px){
          .detail-info-grid{grid-template-columns:1fr !important}
        }
        @media(max-width:480px){
          .od-actions{flex-wrap:wrap}
          .od-actions > button{flex:1 1 calc(50% - 5px)}
          .od-actions > button:last-child{flex:1 1 100%}
        }
      `}</style>
    </div>
  )
}

export default OrderDetail